import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, AlertTriangle, X } from "lucide-react";
import { PushToProdButton } from "@/components/PushToProdButton";

const empty = { slug: "", title: "", summary: "", body: "", category: "Analytical Methods" };

export default function Articles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(empty);
  const [findings, setFindings] = useState([]);
  const [override, setOverride] = useState(false);

  const load = () => api.get("/admin/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const headers = override ? { "X-Compliance-Override": "1" } : {};
      const { data } = await api.post("/admin/articles", form, { headers });
      if (data.blocked) {
        setFindings(data.findings);
        toast.warning(`Blocked terms (${data.findings.length}). Resolve or override.`);
      } else {
        toast.success("Saved");
        setEditing(false); setForm(empty); setFindings([]); setOverride(false);
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (slug) => {
    if (!confirm(`Delete ${slug}?`)) return;
    await api.delete(`/admin/articles/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Library</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Research articles</h1>
        </div>
        <button onClick={() => { setEditing(true); setForm(empty); setFindings([]); }} className="btn-primary inline-flex items-center gap-2" data-testid="new-article"><Plus className="h-4 w-4" /> New article</button>
      </div>

      <div className="mt-6 border border-[#D1D5DB]">
        <table className="w-full data-table">
          <thead><tr><th className="text-left px-4 py-2">Slug</th><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Category</th><th className="text-right px-4 py-2"></th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.slug}>
                <td className="px-4 py-3 font-mono">{a.slug}</td>
                <td className="px-4 py-3">{a.title}</td>
                <td className="px-4 py-3 text-xs">{a.category}</td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button onClick={() => { setEditing(true); setForm({ slug: a.slug, title: a.title, summary: a.summary, body: a.body, category: a.category }); }} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline">Edit</button>
                  <PushToProdButton
                    endpoint={`/admin/articles/${a.slug}/push-to-production`}
                    label={a.slug}
                    entity="article"
                    testid={`article-push-${a.slug}`}
                  />
                  <button onClick={() => del(a.slug)} className="text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setEditing(false)}>
          <div className="w-full max-w-2xl h-full bg-white border-l border-[#D1D5DB] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#D1D5DB] flex justify-between items-center">
              <div className="label-eyebrow">Article</div>
              <button onClick={() => setEditing(false)} className="p-2 hover:bg-[#F7F7F8]"><X className="h-4 w-4" /></button>
            </div>
            <div className="px-6 py-6 space-y-4">
              <Field label="Slug" v={form.slug} on={(v) => setForm({ ...form, slug: v })} test="art-slug" />
              <Field label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} test="art-title" />
              <Field label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} test="art-cat" />
              <div>
                <label className="label-eyebrow block mb-1">Summary</label>
                <textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full p-2 border border-[#D1D5DB] text-sm" data-testid="art-summary" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Body</label>
                <textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full p-2 border border-[#D1D5DB] text-sm" data-testid="art-body" />
              </div>
              {findings.length > 0 && (
                <div className="border border-[#D0021B] bg-[#FCEBED] p-4">
                  <div className="flex items-center gap-2 text-[#A30414] font-semibold"><AlertTriangle className="h-4 w-4" /> Blocked terms ({findings.length})</div>
                  <ul className="mt-3 space-y-1 text-xs font-mono">
                    {findings.slice(0, 12).map((f, i) => <li key={i}><span className="bg-[#D0021B] text-white px-1.5 py-0.5">{f.term}</span> …{f.context}…</li>)}
                  </ul>
                  <label className="mt-3 inline-flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Override (audit logged)
                  </label>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#D1D5DB] flex justify-end gap-3">
              <button onClick={() => setEditing(false)} className="btn-outline">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="save-article">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, v, on, test }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <input value={v} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm" data-testid={test} />
  </div>
);
